import React, { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { ShieldCheck, Search, Save, Info } from "lucide-react";

/**
 * AdminUserPropertyAccess — Super Admin surface.
 *
 * Lists users and lets Super Admin toggle which Properties each user
 * can access in the CRM. Users on default access (based on their role)
 * are shown but flagged so the admin sees when they're relying on
 * defaults vs. an explicit override.
 */
export default function AdminUserPropertyAccess() {
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [grants, setGrants] = useState({}); // user_id -> [slug]
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, gRes, uRes] = await Promise.all([
        api.get("/admin/properties"),
        api.get("/admin/user-property-access"),
        api.get("/admin/users", { params: { limit: 500 } }),
      ]);
      setProperties(pRes.data?.items || []);
      const gmap = {};
      (gRes.data?.items || []).forEach((row) => { gmap[row.user_id] = row.properties || []; });
      setGrants(gmap);
      // /admin/users returns either { items } or an array — handle both.
      const list = Array.isArray(uRes.data) ? uRes.data : (uRes.data?.items || []);
      setUsers(list);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (uid, slug) => {
    setGrants((g) => {
      const current = new Set(g[uid] || []);
      if (current.has(slug)) current.delete(slug); else current.add(slug);
      return { ...g, [uid]: Array.from(current) };
    });
  };

  const save = async (uid) => {
    setSaving((s) => ({ ...s, [uid]: true }));
    try {
      await api.put(`/admin/user-property-access/${uid}`, { properties: grants[uid] || [] });
      toast.success("Access saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, [uid]: false })); }
  };

  const filtered = users.filter((u) => {
    const query = q.trim().toLowerCase();
    if (!query) return true;
    return (
      (u.name || "").toLowerCase().includes(query) ||
      (u.email || "").toLowerCase().includes(query) ||
      (u.role || "").toLowerCase().includes(query)
    );
  });

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="admin-user-property-access">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">SUPER ADMIN · MULTI-VERTICAL ACCESS</div>
          <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#8A6600]" /> User Property Access
          </h1>
          <p className="text-sm text-ink-soft mt-1 max-w-2xl">
            Grant admins access to one or more CRM properties. Users with no explicit grant fall back to a
            role-based default (e.g. debt-team roles auto-see VenturaZ MONEY). Super Admin always sees every property.
          </p>
        </div>

        <div className="relative max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or role…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-ink/15 rounded focus:outline-none focus:border-navy"
            data-testid="upa-search"
          />
        </div>

        <div className="border border-ink/10 rounded-lg bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-ink-muted border-b border-ink/[0.06] bg-[#FBFAF5]">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-5">Properties</div>
            <div className="col-span-1 text-right">Save</div>
          </div>
          {loading && <div className="p-6 text-center text-sm text-ink-muted">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-ink-muted">No users match your search.</div>
          )}
          {!loading && filtered.map((u) => {
            const uid = u.user_id;
            const list = grants[uid];
            const hasExplicit = Array.isArray(list) && list.length >= 0 && grants.hasOwnProperty(uid);
            return (
              <div key={uid} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-ink/[0.05] last:border-0 items-center" data-testid={`upa-row-${uid}`}>
                <div className="col-span-4 min-w-0">
                  <div className="font-heading text-sm text-ink truncate">{u.name || "—"}</div>
                  <div className="text-[11px] text-ink-muted truncate">{u.email}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-ink/15 bg-ink/[0.03]">{u.role}</span>
                  {u.role === "super_admin" && (
                    <div className="text-[9px] mt-1 text-ink-muted italic inline-flex items-center gap-1"><Info size={10} /> owns all properties</div>
                  )}
                </div>
                <div className="col-span-5 flex flex-wrap gap-1.5">
                  {u.role === "super_admin" ? (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted italic">All (implicit)</span>
                  ) : (
                    <>
                      {properties.map((p) => {
                        const on = (list || []).includes(p.slug);
                        return (
                          <button
                            key={p.slug}
                            onClick={() => toggle(uid, p.slug)}
                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border transition-colors ${on ? "bg-navy text-cream border-navy" : "bg-white text-ink border-ink/15 hover:border-navy"}`}
                            data-testid={`upa-chip-${uid}-${p.slug}`}
                            style={on && p.slug === "venturaz_money" ? { background: "#7A4E1D", borderColor: "#7A4E1D" } : undefined}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                      {!hasExplicit && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-ink-muted italic ml-2 self-center">default</span>
                      )}
                    </>
                  )}
                </div>
                <div className="col-span-1 text-right">
                  {u.role !== "super_admin" && (
                    <button
                      onClick={() => save(uid)}
                      disabled={saving[uid]}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-heading rounded bg-navy text-cream hover:bg-[#256E58] disabled:opacity-60"
                      data-testid={`upa-save-${uid}`}
                    >
                      <Save size={12} /> {saving[uid] ? "…" : "Save"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </DashboardShell>
  );
}
